import React from 'react';
import { useTranslation } from 'react-i18next';
import { KSEF_AUTH_METHOD, NUMERATION_SCHEMA_ENDPOINT } from '../../consts';
import { ApiFieldConfigMap, DynamicFieldsConfig, FieldAttributesConfig } from '../../types/dynamicForm';
import { TenantSettings } from '../../types/tenantSettings';

/** The `t` returned by useTranslation, as the config builders receive it. */
type Translate = ReturnType<typeof useTranslation>['t'];

/**
 * How a stored value is rendered on the settings screen.
 * - `sensitive`  a credential the API echoes back: masked behind a reveal toggle.
 * - `writeOnly`  a credential the API only reports as "stored or not": always masked.
 * - `schema`     a numeration schema id, shown as the schema's name.
 * - `multiline`  free text whose line breaks matter (reminder templates).
 * - `certificate` the stored KSeF certificate's subject, expiry and fingerprint.
 */
export type SettingsFieldKind = 'text' | 'boolean' | 'sensitive' | 'writeOnly' | 'schema' | 'multiline' | 'certificate';

export interface SettingsRow {
  /** Key on the settings payload to read - may be a read-only `*_display` twin. */
  key: keyof TenantSettings;
  label: string;
  kind?: SettingsFieldKind;
  /** Hide the row unless this holds - a group with no rows left is hidden too. */
  shownWhen?: (settings: TenantSettings) => boolean;
}

export interface SettingsGroup {
  title: string;
  rows: SettingsRow[];
  /** Writable field names handed to the edit form for this group. */
  editFields: string[];
  /** Offer a button in the edit form that fills this group from other stored settings. */
  copyFrom?: {
    label: string;
    /** Shown on the disabled button while the source is not configured. */
    missingHint: string;
    /** Source field whose value must be set for there to be anything to copy. */
    requiredField: keyof TenantSettings;
    /** Field of this group -> the setting its value is copied from. */
    fields: Record<string, keyof TenantSettings>;
  };
}

export interface SettingsSection {
  title: string;
  description?: string;
  /** Toolbar label for the action that opens this section's edit form. */
  editLabel: string;
  groups: SettingsGroup[];
}

export type SettingsSectionKey = 'general' | 'numbering' | 'invoicing' | 'reminders' | 'automation';

/** A credential row is only shown for the authentication method that uses it. */
const usesKsefCertificate = (settings: TenantSettings) =>
  settings.ksef_auth_method === KSEF_AUTH_METHOD.CERTIFICATE;

/**
 * The tenant settings split by the screen that owns them. Each section is one
 * page and one small edit form, instead of the single form that used to ask
 * for all forty-odd fields at once.
 *
 * Labels are English source strings, translated at render time.
 */
export const SETTINGS_SECTIONS: Record<SettingsSectionKey, SettingsSection> = {
  general: {
    title: 'General',
    description: 'Dashboard thresholds and how equipment counter readings are collected.',
    editLabel: 'Edit general settings',
    groups: [
      {
        title: 'Dashboard',
        rows: [{ key: 'contracts_expiring_soon_days', label: 'Contracts expiring soon days' }],
        editFields: ['contracts_expiring_soon_days'],
      },
      {
        title: 'Counters',
        rows: [
          { key: 'counter_outdated_after_days', label: 'Consider counter value as outdated after days' },
          { key: 'equipment_counter_priority_provider_display', label: 'Counter priority provider' },
        ],
        editFields: ['counter_outdated_after_days', 'equipment_counter_priority_provider'],
      },
      {
        title: 'Counter reading inbox (IMAP)',
        rows: [
          { key: 'imap_server', label: 'IMAP server' },
          { key: 'imap_port', label: 'IMAP port' },
          { key: 'imap_username', label: 'IMAP username', kind: 'sensitive' },
          { key: 'imap_password', label: 'IMAP password', kind: 'sensitive' },
        ],
        editFields: ['imap_server', 'imap_port', 'imap_username', 'imap_password'],
      },
    ],
  },
  numbering: {
    title: 'Numbering',
    description: 'Which numeration schema each kind of document gets by default.',
    editLabel: 'Edit numbering defaults',
    groups: [
      {
        title: 'Numeration schema defaults',
        rows: [
          { key: 'default_job_numeration_schema', label: 'Default numeration schema for jobs', kind: 'schema' },
          { key: 'default_contract_numeration_schema', label: 'Default numeration schema for contracts', kind: 'schema' },
          { key: 'default_ordered_part_numeration_schema', label: 'Default numeration schema for ordered parts', kind: 'schema' },
          { key: 'default_invoice_numeration_schema', label: 'Default numeration schema for invoices', kind: 'schema' },
        ],
        editFields: [
          'default_job_numeration_schema',
          'default_contract_numeration_schema',
          'default_ordered_part_numeration_schema',
          'default_invoice_numeration_schema',
        ],
      },
    ],
  },
  invoicing: {
    title: 'Invoicing',
    description:
      'The seller block printed on every invoice, the defaults new invoices start from, the mail server invoices are emailed through, and the KSeF connection.',
    editLabel: 'Edit invoicing settings',
    groups: [
      {
        title: 'Seller details',
        rows: [
          { key: 'seller_street', label: 'Seller street' },
          { key: 'seller_city', label: 'Seller city' },
          { key: 'seller_zip_code', label: 'Seller zip code' },
          { key: 'seller_country', label: 'Seller country' },
          { key: 'seller_bank_account', label: 'Seller bank account' },
          { key: 'seller_bank_name', label: 'Seller bank name' },
        ],
        editFields: [
          'seller_street',
          'seller_city',
          'seller_zip_code',
          'seller_country',
          'seller_bank_account',
          'seller_bank_name',
        ],
      },
      {
        title: 'Invoice defaults',
        rows: [
          { key: 'default_vat_rate_display', label: 'Default VAT rate' },
          { key: 'default_invoice_payment_method_display', label: 'Default invoice payment method' },
          { key: 'invoice_payment_term_days', label: 'Invoice payment term days' },
          { key: 'default_invoice_grouping_display', label: 'Default invoice grouping' },
          { key: 'invoice_pdf_template_display', label: 'Invoice PDF template' },
        ],
        editFields: [
          'default_vat_rate',
          'default_invoice_payment_method',
          'invoice_payment_term_days',
          'default_invoice_grouping',
          'invoice_pdf_template',
        ],
      },
      {
        title: 'Invoice email server (SMTP)',
        rows: [
          { key: 'invoice_smtp_server', label: 'SMTP server' },
          { key: 'invoice_smtp_port', label: 'SMTP port' },
          { key: 'invoice_smtp_username', label: 'SMTP username', kind: 'sensitive' },
          { key: 'invoice_smtp_password', label: 'SMTP password', kind: 'sensitive' },
        ],
        editFields: ['invoice_smtp_server', 'invoice_smtp_port', 'invoice_smtp_username', 'invoice_smtp_password'],
        copyFrom: {
          label: 'Copy from reminders',
          missingHint: 'No reminders email server is configured.',
          requiredField: 'smtp_server',
          fields: {
            invoice_smtp_server: 'smtp_server',
            invoice_smtp_port: 'smtp_port',
            invoice_smtp_username: 'smtp_username',
            invoice_smtp_password: 'smtp_password',
          },
        },
      },
      {
        title: 'KSeF',
        rows: [
          { key: 'ksef_enabled', label: 'KSeF enabled', kind: 'boolean' },
          { key: 'ksef_environment_display', label: 'KSeF environment' },
          { key: 'ksef_auth_method_display', label: 'KSeF authentication method' },
          { key: 'ksef_token_set', label: 'KSeF token', kind: 'writeOnly', shownWhen: (settings) => !usesKsefCertificate(settings) },
        ],
        editFields: ['ksef_enabled', 'ksef_environment', 'ksef_auth_method', 'ksef_token'],
      },
      {
        // Only shown while the certificate method is selected - on screen via
        // `shownWhen`, in the form via SETTINGS_DYNAMIC_FIELDS.
        title: 'KSeF certificate',
        rows: [
          { key: 'ksef_certificate_info', label: 'KSeF certificate', kind: 'certificate', shownWhen: usesKsefCertificate },
        ],
        editFields: ['ksef_certificate_file', 'ksef_private_key_file', 'ksef_certificate_password'],
      },
    ],
  },
  reminders: {
    title: 'Reminders',
    description: 'The mail server outgoing messages are sent through, and the reminders built on top of it.',
    editLabel: 'Edit reminder settings',
    groups: [
      {
        title: 'Email server (SMTP)',
        rows: [
          { key: 'smtp_server', label: 'SMTP server' },
          { key: 'smtp_port', label: 'SMTP port' },
          { key: 'smtp_username', label: 'SMTP username', kind: 'sensitive' },
          { key: 'smtp_password', label: 'SMTP password', kind: 'sensitive' },
        ],
        editFields: ['smtp_server', 'smtp_port', 'smtp_username', 'smtp_password'],
      },
      {
        title: 'Counter reminders',
        rows: [
          { key: 'next_reminder_available_after_days', label: 'Next reminder available after days' },
          { key: 'reminder_template_title', label: 'Reminder template title', kind: 'multiline' },
          { key: 'reminder_template_message', label: 'Reminder template message', kind: 'multiline' },
        ],
        editFields: [
          'next_reminder_available_after_days',
          'reminder_template_title',
          'reminder_template_message',
        ],
      },
      {
        title: 'Obligatory service reminders',
        rows: [
          {
            key: 'service_reminder_template_title',
            label: 'Service reminder template title',
            kind: 'multiline',
          },
          {
            key: 'service_reminder_template_message',
            label: 'Service reminder template message',
            kind: 'multiline',
          },
        ],
        editFields: [
          'service_reminder_template_title',
          'service_reminder_template_message',
        ],
      },
    ],
  },
  automation: {
    title: 'Settlement automation',
    editLabel: 'Edit automation settings',
    groups: [
      {
        title: 'Automation run',
        rows: [
          { key: 'automation_run_day', label: 'Settlement automation run day' },
          { key: 'proration_cutoff_day', label: 'Proration cutoff day' },
          { key: 'job_cost', label: 'Mean job execution cost' },
        ],
        editFields: [
          'automation_run_day',
          'proration_cutoff_day',
          'job_cost',
        ],
      },
    ],
  },
};

/** Writable field names of a whole section, in the order they are displayed. */
export const sectionEditFields = (section: SettingsSectionKey): string[] =>
  SETTINGS_SECTIONS[section].groups.flatMap((group) => group.editFields);

/** Numeration schema pickers, shared by every form that offers one. */
export const SETTINGS_API_FIELDS: ApiFieldConfigMap = Object.fromEntries(
  [
    'default_job_numeration_schema',
    'default_contract_numeration_schema',
    'default_ordered_part_numeration_schema',
    'default_invoice_numeration_schema',
  ].map((field) => [field, { url: NUMERATION_SCHEMA_ENDPOINT, field: 'name' }])
);

const ksefCertificateRule = [
  { dependsOn: 'ksef_auth_method', value: KSEF_AUTH_METHOD.CERTIFICATE, action: 'show' as const },
];

/**
 * Only the credential fields of the selected KSeF method are shown, and hidden
 * fields are not submitted.
 */
export const SETTINGS_DYNAMIC_FIELDS: DynamicFieldsConfig = {
  ksef_token: [{ dependsOn: 'ksef_auth_method', value: KSEF_AUTH_METHOD.TOKEN, action: 'show' }],
  ksef_certificate_file: ksefCertificateRule,
  ksef_private_key_file: ksefCertificateRule,
  ksef_certificate_password: ksefCertificateRule,
};

/**
 * Per-field form hints. Kept next to the section definitions so a field and
 * the sentence explaining it never drift apart.
 */
export const buildSettingsFieldAttributes = (t: Translate): FieldAttributesConfig => ({
  imap_password: { type: 'password' },
  smtp_password: { type: 'password' },
  invoice_smtp_server: {
    helperText: t(
      "Leave empty to send invoices through the reminders mail server, or from the app's address if that is not configured either."
    ),
  },
  invoice_smtp_password: { type: 'password' },
  default_invoice_grouping: {
    helperText: t(
      'How settlement automation gathers confirmed settlements into invoices. A client can override it in its invoice defaults.'
    ),
  },
  ksef_token: { type: 'password' },
  ksef_certificate_file: {
    file: { accept: '.crt,.cer,.pem,.der,.p12,.pfx' },
    helperText: t(
      'The certificate (.crt, .cer, .pem) or a PKCS#12 bundle (.p12, .pfx). Leave empty to keep the stored certificate.'
    ),
  },
  ksef_private_key_file: {
    file: { accept: '.key,.pem,.der' },
    helperText: t('The private key (.key, .pem). Not needed for a PKCS#12 bundle.'),
  },
  ksef_certificate_password: {
    type: 'password',
    fullRow: true,
    helperText: t(
      'Password protecting the private key or the PKCS#12 bundle, if it has one. Used only to read the files, never stored.'
    ),
  },
  automation_run_day: {
    helperText: t('Day of the month (1-31) on which settlement automation runs for opted-in contracts.'),
  },
  proration_cutoff_day: {
    helperText: t(
      'For a term that ended this month: only prorate its final settlement if the end counter falls on or before this day (1-31). Past it, it bills a full period instead. Leave empty to always prorate.'
    ),
  },
  job_cost: {
    helperText: t(
      'Average cost of executing a single service job. Used in settlement profitability for jobs with no priced service - a job with priced services costs their prices instead.'
    ),
  },
  equipment_counter_priority_provider: {
    helperText: t(
      'When set, settlement automation only picks up new counter readings from this provider and ignores readings from any other provider (a Contract can override this). Staff can still manually pick another reading for a specific settlement. Leave empty to keep picking the latest reading regardless of provider.'
    ),
  },
  reminder_template_title: {
    multiline: true,
    minRows: 2,
    helperText: t('Tag details are listed below. You can use the same tags in the title.'),
  },
  reminder_template_message: {
    multiline: true,
    minRows: 5,
    helperText: (
      <>
        {t('Reminder templates support placeholders that are replaced with actual values when sending.')}
        <br />
        <strong>{t('Available tags')}:</strong>
        <br />
        {'{'}CLIENT_NAME{'}'} - {t('client name')}
        <br />
        {'{'}EQUIPMENTS_DETAILS{'}'} - {t('full equipment details (name and serial, bullet list)')}
        <br />
        {'{'}EQUIPMENTS_NAMES{'}'} - {t('equipment names (bullet list)')}
        <br />
        {'{'}EQUIPMENTS_SERIALS{'}'} - {t('equipment serial numbers (bullet list)')}
        <br />
        {'{'}EQUIPMENTS_ADDRESSES{'}'} - {t('equipment installation addresses (bullet list)')}
        <br />
        {'{'}COMPANY_NAME{'}'} - {t('workspace or company name')}
      </>
    ),
  },
  service_reminder_template_title: {
    multiline: true,
    minRows: 2,
    helperText: t('Tag details are listed below. You can use the same tags in the title.'),
  },
  service_reminder_template_message: {
    multiline: true,
    minRows: 5,
    helperText: (
      <>
        {t(
          'Sent to a buyer ahead of an obligatory service date on a sale-and-service contract.'
        )}
        <br />
        <strong>{t('Available tags')}:</strong>
        <br />
        {'{'}CLIENT_NAME{'}'} - {t('client name')}
        <br />
        {'{'}DUE_DATE{'}'} - {t('date the service is due')}
        <br />
        {'{'}WARRANTY_UNTIL{'}'} - {t('date the guarantee runs out')}
        <br />
        {'{'}EQUIPMENTS_DETAILS{'}'} - {t('full equipment details (name and serial, bullet list)')}
        <br />
        {'{'}EQUIPMENTS_NAMES{'}'} - {t('equipment names (bullet list)')}
        <br />
        {'{'}EQUIPMENTS_SERIALS{'}'} - {t('equipment serial numbers (bullet list)')}
        <br />
        {'{'}EQUIPMENTS_ADDRESSES{'}'} - {t('equipment installation addresses (bullet list)')}
        <br />
        {'{'}COMPANY_NAME{'}'} - {t('workspace or company name')}
      </>
    ),
  },
});
